import { getAccessToken } from './authService';
import { authenticatedGet, authenticatedPost, authenticatedPut } from '../utils/apiUtils';
import config from '../config/config';
import { getOfferAiSuggestion, regenerateOfferAiSuggestion } from '../services/api';

const API_BASE_URL = `${config.apiUrl}/offers`;


export const createOffer = async (willId: string, offerData: any) => {
    try {
        const response = await authenticatedPost(`${API_BASE_URL}/will/${willId}`, offerData);
        
        if (!response.ok) {
            throw new Error('Failed to create offer');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error creating offer:', error);
        throw error;
    }
};

export const getMyGenies = async (page = 0, size = 10) => {
    try {
        const response = await authenticatedGet(
            `${import.meta.env.VITE_API_URL}/offers/received?page=${page}&size=${size}&sortBy=createdAt&direction=DESC`
        );
        
        if (!response.ok) {
            throw new Error('Failed to fetch received offers');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error fetching received offers:', error);
        throw error;
    }
};

export const getProviderOffers = async (providerId: string) => {
    try {
        const response = await authenticatedGet(`${import.meta.env.VITE_API_URL}/offers/provider/${providerId}`);
        
        if (!response.ok) {
            throw new Error('Failed to fetch provider offers');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error fetching provider offers:', error);
        throw error;
    }
};

export const getFeed = async (size: number) => {
    try {
        const response = await authenticatedGet(`${import.meta.env.VITE_API_URL}/feed/filtered?size=${size}`);
        
        if (!response.ok) {
            throw new Error('Failed to fetch feed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error fetching feed:', error);
        throw error;
    }
}; 

export const updateOfferStatus = async (offerId: string, status: string) => {
    try {
        const response = await authenticatedPut(
            `${import.meta.env.VITE_API_URL}/offers/${offerId}/status`,
            { status }
        );
        
        if (!response.ok) {
            throw new Error(`Failed to update offer status to ${status}`);
        }
        
        return await response.json();
    } catch (error) {
        console.error(`Error updating offer status to ${status}:`, error);
        throw error;
    }
};

export const getOfferById = async (offerId: string) => {
    try {
        const response = await authenticatedGet(
            `${import.meta.env.VITE_API_URL}/offers/${offerId}`
        );
        
        if (!response.ok) {
            throw new Error('Failed to fetch offer details');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error fetching offer details:', error);
        throw error;
    }
};

export const createOfferWithMedia = async (willId, offerData, mediaFiles) => {
    const formData = new FormData();
    
    // Add offer data
    formData.append('offer', JSON.stringify(offerData));
    
    console.log('Sending offer data:', offerData);
    console.log('Media files:', mediaFiles);
    
    // Add media files - ensure files are being added correctly
    if (mediaFiles && Array.isArray(mediaFiles) && mediaFiles.length > 0) {
        mediaFiles.forEach((file, index) => {
            if (file instanceof File) {
                console.log(`Adding file ${index}:`, file.name, file.type, file.size);
                formData.append('files', file);
            } else {
                console.error(`Invalid file object at index ${index}:`, file);
            }
        });
    } else {
        console.log('No valid media files to upload');
    }
    
    // Check formData contents
    for (let pair of formData.entries()) {
        console.log('FormData entry:', pair[0], pair[1]);
    }
    
    const token = getAccessToken();
    
    try {
        // Use the updated endpoint with willId in the path
        const response = await fetch(`${config.apiUrl}/offers/will/${willId}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('Server error response:', errorText);
            throw new Error(`Failed to create offer with media: ${errorText}`);
        }
        
        const result = await response.json();
        console.log('Create offer response:', result);
        return result;
    } catch (error) {
        console.error('Error creating offer with media:', error);
        throw error;
    }
};

export const updateOfferWithMedia = async (offerId, offerData, mediaFiles) => {
    const formData = new FormData();
    
    // Add offer data
    formData.append('offer', JSON.stringify(offerData));
    
    // Add media files
    mediaFiles.forEach((file) => {
        formData.append(`files`, file);
    });
    
    const token = getAccessToken();
    
    try {
        const response = await fetch(`${config.apiUrl}/offers/${offerId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });
        
        if (!response.ok) {
            throw new Error('Failed to update offer with media');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error updating offer with media:', error);
        throw error;
    }
};

/**
 * Fetch an AI-generated suggestion for an offer based on the will details
 */
export const getAiSuggestedOffer = async (willId: string) => {
    try {
        // Using the api function directly
        const response = await getOfferAiSuggestion(willId);
        console.log('Raw AI suggestion response:', response);
        
        // If the response is a string, try to parse it as JSON
        if (typeof response === 'string') {
            try {
                const parsedResponse = JSON.parse(response);
                return parsedResponse;
            } catch (e) {
                console.warn('AI suggestion response is not valid JSON:', e);
                // Return as-is if parsing fails
                return response;
            }
        }
        
        return response;
    } catch (error) {
        console.error('Error fetching AI suggestion for offer:', error);
        throw error;
    }
};

/**
 * Regenerate an AI suggestion for an offer
 */
export const regenerateAiSuggestion = async (willId: string) => {
    try {
        // Using the api function directly
        const response = await regenerateOfferAiSuggestion(willId);
        console.log('Raw AI regenerated suggestion response:', response);
        
        // If the response is a string, try to parse it as JSON
        if (typeof response === 'string') {
            try {
                const parsedResponse = JSON.parse(response);
                return parsedResponse;
            } catch (e) {
                console.warn('AI regenerated suggestion response is not valid JSON:', e);
                // Return as-is if parsing fails
                return response;
            }
        }
        
        return response;
    } catch (error) {
        console.error('Error regenerating AI suggestion:', error);
        throw error;
    }
};
